import {useCallback, useEffect} from 'react';
// Redux
import {useSelector, useDispatch} from 'react-redux';
import {appConfig, gaConfig} from '@stencil/hotels-config';
// Styles
import * as S from './styles/PreviousBookingQuickBook.styles';
import {
  BaseColumnWrap,
  HiddenElement,
} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import {defaultTheme} from '@stencil/build-theme';
// Selectors
import {
  contextDetailsSelector,
  hotelLocationSelector,
  hotelNameSelector,
  isEntirePropertySelector,
  searchHotelDetailsSelector,
  luckyDataSelector,
  isAltAccoSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';
import {roomStringSelectorFn} from '@stencil/hotels-common-reducer/src/selectors';
// // Icons
import HappyTickMarkIcon from '@stencil/icons/src/HappyTickMarkIcon/HappyTickMarkIcon';
import FreeCancelIcon from '../../../../../stories/Icons/FreeCancelIcon';
import NonRefundableIcon from '../../../../../stories/Icons/NonRefundable';
import RightDoubleArrowIcon from '../../../../../stories/Icons/RightDoubleArrow';
// Conponents
import PreviousBookingBookNowButton from '@stencil/book-now-previous-booking';
import RoomInfoText from '../RoomSelection/RoomInfoText';
// Thunks
import {detailsPagePushGaThunk} from '@stencil/hotels-ga/src';
// utils
import {detailsNavigationIdsAndValues} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import {scrollIntoViewDetails} from '@stencil/hotels-utils/src/scroll-into-view-details';
import getCgNoOfRoomFlavors from '@stencil/hotels-utils/src/get-cg-no-of-room-flavors';
import {getSlashAndShowPriceCg} from '@stencil/hotels-utils/src/get-details-slash-and-show-price';
// Omnitures
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';
import {createPaxStringFromRoomTariffs} from '@stencil/hotels-utils/src/common-utils';
import CheckAvailabilityAltAcco from '../../../../../stories/components/hotels/DetailsPageUnif/CheckAvailabilityAltAcco/CheckAvailabilityAltAcco';
import useCurrentRoomData from '../../../../hooks/useCurrentRoomData';

export default function DetailQuickBookCardUI({
  bookingwigitdata = {},
  isNoPreviousBooking = false,
  isDatePickRequired = false,
  altAcco = false,
  handlOpenCalendar = () => {},
}) {
  const dispatch = useDispatch();
  const hotelDetails = useSelector(searchHotelDetailsSelector);
  const contextDetails = useSelector(contextDetailsSelector);
  const hotelName = useSelector(hotelNameSelector);
  const hotelLocation = useSelector(hotelLocationSelector);
  const isEntireProperty = useSelector(isEntirePropertySelector);
  const isAltAcco = useSelector(isAltAccoSelector);
  const luckyData = useSelector(luckyDataSelector);
  const roomString = useSelector(roomStringSelectorFn);

  const roomCount = roomString?.split('-')?.[0];

  const {activeBankCode} = useCurrentRoomData();

  const hotelArea = hotelLocation?.name;

  const totalNoOfRooms =
    (bookingwigitdata?.exactRooms &&
      bookingwigitdata?.exactRooms.length &&
      getCgNoOfRoomFlavors(bookingwigitdata?.exactRooms)) ||
    0;

  const {rupeeString, multiRoomDefaultNoImagePlacholder} = appConfig;
  useEffect(() => {
    if (!isNoPreviousBooking) {
      dispatch(
        detailsPagePushGaThunk({
          event: gaConfig.detailsPageClickEventCategoryName,
          eventCategory: gaConfig.detailsPageClickEventCategoryName,
          eventAction: gaConfig.DetailPageGAEvents.detailsQuickBook.quickbook_HD_shown + '_1',
        }),
      );
    }
  }, []);
  const onBookNowClick = () => {
    if (!isNoPreviousBooking) {
      dispatch(
        omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
          cta: {
            name: `${DetailsPageComponents.QuickBookCard}_${detailsClick.detailsQuickBook.quickbook_HD_click}_1`,
            type: omnitureConfig.ctaType.Button,
            componentName: DetailsPageComponents.QuickBookCard,
            destination: '',
          },
        }),
      );
    }
  };

  const handleOnClickViewMoeRoom = useCallback(() => {
    scrollIntoViewDetails(
      document.querySelector(
        isAltAcco ? detailsNavigationIdsAndValues.stays.id : detailsNavigationIdsAndValues.rooms.id,
      ),
    );
  }, [isAltAcco, detailsNavigationIdsAndValues, scrollIntoViewDetails]);

  return (
    <>
      {' '}
      {bookingwigitdata?.exactRooms?.map((item, index) => {
        const roomCode = item?.roomCode;
        const roomName = item?.roomName;

        // const maxNoOfAdults = staticDataForThisRoom?.maxAdult;
        const roomImage = item?.images?.[0];

        const roomFacilities =
          item?.amenities?.[0].facilities
            .filter((item, index) => index < 2)
            .map(item => item?.name) || [];
        return item?.ratePlans.map(roomPlanItem => {
          if (roomPlanItem) {
            const refundStatus =
              roomPlanItem?.cancellationPolicy?.type === 'FC' ||
              roomPlanItem?.cancellationPolicy?.type === 'FCC';
            const refundStatusText = roomPlanItem?.cancellationPolicy?.text;
            const refundStatusTextColor = refundStatus ? 'green' : 'red';
            const {slashedPrice, showPrice, tnc, finalPriceKey} = getSlashAndShowPriceCg(
              roomPlanItem?.tariffs?.[0],
              activeBankCode,
            );
            const tariffs = roomPlanItem?.tariffs?.[0] || {};
            const pax = createPaxStringFromRoomTariffs(tariffs?.roomTariffs);
            const updatedRoomPlanItem = {
              fwdp: tariffs?.hermesData?.[0]?.paxWiseInfo?.[0]?.forwardParams,
              rpc: roomPlanItem?.rpc,
              offercode: finalPriceKey || tariffs?.hermesData?.[0]?.offerCode,
              ibp: hotelDetails?.supplierId,
              pm: roomPlanItem?.payMode,
              rtc: roomCode,
              gseg: tariffs?.hermesData?.[0]?.segmentId,
              cot: tariffs?.hermesData?.[0]?.contractType,
              hmd: tariffs?.hermesData?.[0]?.paxWiseInfo?.[0]?.hermesMetaData,
              gsv: roomPlanItem?.supplierCode,
              pc: tariffs?.hermesData?.[0]?.promoCode,
              cur: contextDetails?.currency,
              pd: {
                pax,
                spr: showPrice,
              },
              payMode: roomPlanItem?.payMode,
              mtKey: tariffs?.mtKey,
              isLucky: roomPlanItem?.isLucky,
            };
            return (
              <>
                <S.DetailQuickBookWrapper>
                  <S.RoomDetailsWrapper>
                    <BaseColumnWrap
                      style={{
                        width: '60%',
                      }}
                    >
                      <S.RoomKindPara title={roomName}>
                        {!!roomCount && `${roomCount} x `}
                        {roomName}
                      </S.RoomKindPara>
                      <S.RoomDescriptionWrapper>
                        {item?.roomHighlights
                          ?.filter(highlight => highlight?.identifier !== 'GUEST_COUNT')
                          ?.map(highlights => {
                            return (
                              <li
                                style={{
                                  padding: '2px 0px',
                                  display: 'flex',
                                }}
                              >
                                <span>
                                  <img
                                    src={highlights?.iconUrl}
                                    height="14"
                                    width="14"
                                    style={{
                                      marginRight: '0.8rem',
                                    }}
                                  />
                                </span>
                                <span>{highlights?.text}</span>
                              </li>
                            );
                          })}
                      </S.RoomDescriptionWrapper>
                    </BaseColumnWrap>
                    <S.RoomImageOuterWrapper>
                      {!isNoPreviousBooking ? (
                        <S.PreviousStayTag>
                          <S.NewTagLeftMarkup />
                          <S.NewTagRightMarkup />
                          <S.PreviousStayTextSpan>{'Your Previous Stay'}</S.PreviousStayTextSpan>
                        </S.PreviousStayTag>
                      ) : null}
                      <S.RoomImage
                        src={roomImage || multiRoomDefaultNoImagePlacholder}
                        isDefault={!roomImage}
                      />
                    </S.RoomImageOuterWrapper>
                  </S.RoomDetailsWrapper>
                  <S.RoomFacilityWrapper>
                    <S.RoomPlanTag>Plan Includes</S.RoomPlanTag>

                    {roomFacilities && roomFacilities?.length > 0 ? (
                      <li
                        style={{
                          margin: '5px 0px',
                        }}
                      >
                        <HappyTickMarkIcon
                          height="1rem"
                          width="1rem"
                          margin="0 0.8rem 0 0"
                          fill={defaultTheme.colors.downriver.base}
                        />
                        <span>{roomFacilities.join(', ')}</span>
                      </li>
                    ) : null}

                    <li>
                      {refundStatusText && (
                        <RoomInfoText
                          textColor={refundStatusTextColor}
                          alignItems="center"
                          spacedOutBy={'0px'}
                        >
                          <S.RoomOptionsIconContainer>
                            {!!refundStatus ? (
                              <FreeCancelIcon
                                width="1.3rem"
                                height="1.3rem"
                                fill={refundStatusTextColor}
                              />
                            ) : (
                              <NonRefundableIcon
                                width="1.3rem"
                                height="1.3rem"
                                fill={refundStatusTextColor}
                                transform="scale(1.3)"
                              />
                            )}
                          </S.RoomOptionsIconContainer>

                          <S.CancellationPolicyContainer>
                            <span>{refundStatusText}</span>
                          </S.CancellationPolicyContainer>
                        </RoomInfoText>
                      )}
                    </li>

                    <li>
                      <S.AmounttobePiad>
                        {rupeeString} {showPrice}
                      </S.AmounttobePiad>
                      {tnc ? (
                        <S.PerNeightSpan>
                          + {rupeeString} {tnc} taxes & fees
                        </S.PerNeightSpan>
                      ) : null}
                    </li>
                  </S.RoomFacilityWrapper>
                  {!!showPrice && (
                    <HiddenElement itemProp="priceRange">
                      {`${hotelName} Price Starts at INR ${showPrice}/Night`}
                    </HiddenElement>
                  )}
                  <S.BookingRoomWrapper>
                    {isDatePickRequired && altAcco ? (
                      <CheckAvailabilityAltAcco handlOpenCalendar={handlOpenCalendar} />
                    ) : (
                      <>
                        <S.ViewMoreRoom onClick={handleOnClickViewMoeRoom}>
                          {!isNoPreviousBooking
                            ? `${totalNoOfRooms}${!isEntireProperty && ` Room`} Options`
                            : `View ${isEntireProperty ? 'Property' : 'Room'} Details`}

                          {!isNoPreviousBooking ? (
                            <S.RightDoubleArrowIconContainer>
                              <RightDoubleArrowIcon
                                width="1.2rem"
                                height="0.9rem"
                                fill={defaultTheme.colors.goBrandBlue.base}
                              />
                            </S.RightDoubleArrowIconContainer>
                          ) : null}
                        </S.ViewMoreRoom>
                        {roomPlanItem && (
                          <PreviousBookingBookNowButton
                            roomPlanData={updatedRoomPlanItem}
                            roomTypeName={roomName}
                            cityid={hotelDetails?.voyagerCityId}
                            hotelData={{
                              area: hotelArea,
                              name: hotelName,
                            }}
                            onBookNowClick={() => onBookNowClick()}
                            width={'43%'}
                            borderRadius={'24px'}
                            luckyData={luckyData}
                            ratePlanData={roomPlanItem}
                            roomCode={roomCode}
                            searchType={'E'}
                            promoCode={finalPriceKey}
                          />
                        )}
                      </>
                    )}
                  </S.BookingRoomWrapper>
                </S.DetailQuickBookWrapper>
              </>
            );
          }
        });
      })}
    </>
  );
}
